import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { Table, useTableState } from 'modules/ui/table';
import { Loader } from 'modules/ui/loader';
import { useMessages } from 'modules/messages/hooks/useMessages';
import { dateConfig } from 'modules/app/constants';
import { changeCurrency } from 'modules/financial/utils';
import { secToMin } from 'modules/call/utils';

import { CustomerData } from '../interfaces';
import { getAllCustomers, setCustomStripeFlag, setAutoRecordingFlag, setAllowFinancialTechnician } from '../hooks/crud';
import { Box, Checkbox, Typography } from '@mui/material';

import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const CustomerList = () => {
  const { t } = useTranslation('common');
  const { show } = useMessages();
  const { toDollar } = changeCurrency();

  const queryClient = useQueryClient();

  const { mutate: toggleStripeFlag } = useMutation(
    ({ companyId, checked }: { companyId: number; checked: boolean }) =>
      setCustomStripeFlag(companyId, checked),
    {
      onSuccess: () => {
        show({ message: t('general.messages.success'), severity: 'success' });
        queryClient.invalidateQueries(['allCustomers']);
      },
      onError: (error: any) => {
        show({
          message: `${t('general.messages.failed')}. ${error?.message || error}`,
          severity: 'error',
        });
      },
    }
  );

  const { mutate: toggleRecordingFlag } = useMutation(
    ({ companyId, checked }: { companyId: number; checked: boolean }) =>
      setAutoRecordingFlag(companyId, checked),
    {
      onSuccess: () => {
        show({ message: t('general.messages.success'), severity: 'success' });
        queryClient.invalidateQueries(['allCustomers']);
      },
      onError: (error: any) => {
        show({
          message: `${t('general.messages.failed')}. ${error?.message || error}`,
          severity: 'error',
        });
      },
    }
  );

  const { mutate: toggleFinancialTechnicianFlag } = useMutation(
    ({ companyId, checked }: { companyId: number; checked: boolean }) =>
      setAllowFinancialTechnician(companyId, checked),
    {
      onSuccess: () => {
        show({ message: t('general.messages.success'), severity: 'success' });
        queryClient.invalidateQueries(['allCustomers']);
      },
      onError: (error: any) => {
        show({
          message: `${t('general.messages.failed')}. ${error?.message || error}`,
          severity: 'error',
        });
      },
    }
  );

  const [tableState, onChangeTableState] = useTableState({
    orderBy: [{ direction: 'desc', field: 'created_at' }],
  });

  const {
    data: customers,
    isError,
    isLoading,
    error,
  } = useQuery<CustomerData[]>(['allCustomers'], () => getAllCustomers());

  useEffect(() => {
    if (isError && error) {
      show({ message: `${t('general.messages.failed')}. ${error}` });
    }
  }, [error, isError, show, t]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'business_name',
        headerName: `${t('customer.list.companyName')}`,
        valueGetter: ({ value }: GridValueGetterParams) => value || '-',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'created_at',
        headerName: `${t('customer.list.joined')}`,
        valueFormatter: ({ value }) => (value ? dayjs(value).format(dateConfig.format.date) : '-'),
        flex: 1,
        minWidth: 110,
      },
      {
        field: 'admin_name',
        headerName: `${t('customer.list.adminName')}`,
        valueGetter: ({ value }: GridValueGetterParams) => value || '-',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'admin_phone',
        headerName: `${t('customer.list.adminPhone')}`,
        valueGetter: ({ value }: GridValueGetterParams) => value || '-',
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'account_number',
        headerName: `${t('customer.list.achAccount')}`,
        valueGetter: ({ value }: GridValueGetterParams) => value || '-',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'routing_number',
        headerName: `${t('customer.list.achRouting')}`,
        valueGetter: ({ value }: GridValueGetterParams) => value || '-',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'stats.total_calls',
        headerName: `${t('customer.list.callsTotal')}`,
        valueGetter: params => params.row.stats.total_calls || '-',
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'stats.monthly_calls',
        headerName: `${t('customer.list.callsMonth')}`,
        valueGetter: params => params.row.stats.monthly_calls || '-',
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'stats.total_fees',
        headerName: `${t('customer.list.feesTotal')}`,
        valueGetter: params =>
          params.row.stats.total_fees ? `${params.row.stats.currency_symbol}${toDollar(params.row.stats.total_fees)}` : '-',
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'stats.monthly_fees',
        headerName: `${t('customer.list.feesMonth')}`,
        valueGetter: params =>
          params.row.stats.monthly_fees ? `${params.row.stats.currency_symbol}${toDollar(params.row.stats.monthly_fees)}` : '-',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'stats.monthly_avg_call_duration',
        headerName: `${t('customer.list.avgCallDuration')}`,
        valueGetter: params =>
          params.row.stats.monthly_avg_call_duration
            ? `${secToMin(params.row.stats.monthly_avg_call_duration)} mins`
            : '-',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'is_custom_stripe',
        headerName: `${t('customer.list.isCustomStripe')}`,
        renderCell: params => (
          <Checkbox
            checked={params.row.is_custom_stripe}
            onChange={(e) =>
              toggleStripeFlag({ companyId: params.row.id, checked: e.target.checked })
            }
            color="primary"
          />
        ),
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        width: 100,
        minWidth: 100,
      },
      {
        field: 'is_auto_recording_enabled',
        headerName: `${t('customer.list.isAutoRecording')}`,
        renderCell: params => (
          <Checkbox
            checked={params.row.is_auto_recording_enabled}
            onChange={(e) =>
              toggleRecordingFlag({ companyId: params.row.id, checked: e.target.checked })
            }
            color="primary"
          />
        ),
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        width: 100,
        minWidth: 100,
      },
      {
        field: 'allow_financial_technician',
        headerName: `${t('customer.list.allowFinancialTechnician')}`,
        renderCell: params => (
          <Checkbox
            checked={params.row.allow_financial_technician}
            onChange={(e) =>
              toggleFinancialTechnicianFlag({ companyId: params.row.id, checked: e.target.checked })
            }
            color="primary"
          />
        ),
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        width: 100,
        minWidth: 100,
      },
      {
        field: 'external_api_key',
        headerName: `${t('customer.list.externalApiKey')}`,
        renderCell: (params) => {
          const apiKey = params.row.external_api_key;
      
          const handleCopy = () => {
            navigator.clipboard.writeText(apiKey).then(() => {
              show({ message: t('general.messages.copiedToClipboard'), severity: 'success' });
            });
          };
      
          return (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Typography
                variant="body2"
                noWrap
                sx={{ maxWidth: 'calc(100% - 40px)', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {apiKey || '-'}
              </Typography>
              {apiKey && (
                <Tooltip title={t('general.button.copy')}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        minWidth: 250,
      }
    ],
    [t, toDollar],
  );

  return (
    <>
      <Loader isLoading={isLoading} />

      <Table
        loading={isLoading}
        columns={columns}
        rows={customers || []}
        rowCount={customers ? customers.length : 0}
        state={tableState}
        onChangeState={onChangeTableState}
      />
    </>
  );
};
